/**
 * JobHistoryPage — /jobs
 *
 * US-24-GR-12 — Paginated job history table with drill-down drawer.
 */

import { useState, useEffect, useCallback, startTransition } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Download, RefreshCw } from 'lucide-react';
import {
  getJobs,
  getJobTrace,
  cancelJob,
  exportJobsCsv,
  JobAlreadyTerminalError,
  type AgentJobSummaryDto,
  type AgentJobStepDto,
  type JobTraceResult,
  type JobStatus,
  type GetJobsParams,
  type QualityVerdict,
} from '../api/orchestratorClient';
import { useLatestRequest } from '../hooks/useLatestRequest';
import { Table, type Column } from '../components/Table';
import { Drawer } from '../components/Drawer';
import { CancelJobModal } from '../components/CancelJobModal';
import { Toast, type ToastState } from '../components/Toast';
import { AbbrLabel } from '../components/AbbrLabel';
import {
  TimeWindowSelector,
  loadStoredWindow,
  saveStoredWindow,
  type TimeWindow,
} from '../components/TimeWindowSelector';
import { triggerCsvBlobDownload, buildCsvFilename } from '../utils/downloadCsv';

// ---------------------------------------------------------------------------
// Status pill
// ---------------------------------------------------------------------------

function StatusPill({ status }: { status: JobStatus }) {
  const colors: Record<JobStatus, string> = {
    Queued:    'bg-zinc-700 text-zinc-300',
    Running:   'bg-blue-500/20 text-blue-300 border border-blue-500/30',
    Blocked:   'bg-amber-500/20 text-amber-300 border border-amber-500/30',
    Completed: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30',
    Failed:    'bg-red-500/20 text-red-300 border border-red-500/30',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-zinc-700 text-zinc-300'}`}>
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Quality verdict pill — WS-3 item 2.1
// ---------------------------------------------------------------------------

const VERDICT_LABELS: Record<QualityVerdict, string> = {
  passed:         'Passed',
  failed_flagged: 'Flagged',
  skipped:        'Skipped',
};

function QualityPill({
  verdict,
  score,
}: {
  verdict: QualityVerdict | null;
  score: number | null;
}) {
  if (!verdict) return <span className="text-xs text-zinc-600">—</span>;

  const colors: Record<QualityVerdict, string> = {
    passed:         'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30',
    failed_flagged: 'bg-red-500/20 text-red-300 border border-red-500/30',
    skipped:        'bg-zinc-700 text-zinc-300',
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[verdict]}`}>
        {VERDICT_LABELS[verdict]}
      </span>
      {score !== null && score !== undefined && (
        <span className="text-xs text-zinc-400">{Math.round(score * 100)}%</span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatDuration(ms: number | null): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function truncate(id: string | null, len = 8): string {
  if (!id) return '—';
  return id.length > len ? id.slice(0, len) + '…' : id;
}

/** US-24-GR-23 — `$0.0132` (4 dp under $1, 2 dp above); em-dash when unpriced (null). */
function formatUsd(value: number | null): string {
  if (value === null || value === undefined) return '—';
  return `$${value.toFixed(Math.abs(value) < 1 ? 4 : 2)}`;
}

// ---------------------------------------------------------------------------
// Step timeline in the drawer
// ---------------------------------------------------------------------------

function StepTimeline({ steps }: { steps: AgentJobStepDto[] }) {
  if (steps.length === 0) {
    return (
      <p className="text-xs text-zinc-500 italic py-4">
        No detailed trace available — this job ran before step traceability was enabled.
      </p>
    );
  }

  return (
    <ol className="flex flex-col gap-2">
      {steps.map((step) => (
        <li key={step.stepNumber} className="border border-zinc-800 rounded-lg overflow-hidden">
          <details>
            <summary className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-800/50 list-none">
              <span
                aria-label={step.success ? 'Success' : 'Failed'}
                className={`w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0 ${
                  step.success ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                }`}
              >
                {step.success ? '✓' : '✗'}
              </span>
              <span className="text-xs text-zinc-400 font-mono w-5">{step.stepNumber}</span>
              <span className="flex-1 text-xs font-medium text-zinc-200">{step.toolName}</span>
              <span className="text-xs text-zinc-500">{formatDuration(step.durationMs)}</span>
              {(step.inputTokens !== null || step.outputTokens !== null) && (
                <span className="text-xs text-zinc-600">
                  {step.inputTokens ?? 0}↑ {step.outputTokens ?? 0}↓
                </span>
              )}
            </summary>

            <div className="border-t border-zinc-800 px-3 py-3 space-y-3">
              {step.promptText && (
                <div>
                  <p className="text-xs font-semibold text-zinc-400 mb-1">Prompt</p>
                  <pre className="text-xs text-zinc-300 bg-zinc-950 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words font-mono">
                    {step.promptText}
                  </pre>
                </div>
              )}
              {step.responseText && (
                <div>
                  <p className="text-xs font-semibold text-zinc-400 mb-1">Response</p>
                  <pre className="text-xs text-zinc-300 bg-zinc-950 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words font-mono">
                    {step.responseText}
                  </pre>
                </div>
              )}
              {!step.promptText && step.toolInput && (
                <div>
                  <p className="text-xs font-semibold text-zinc-400 mb-1">Tool Input</p>
                  <pre className="text-xs text-zinc-300 bg-zinc-950 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words font-mono">
                    {step.toolInput}
                  </pre>
                </div>
              )}
              {!step.responseText && step.toolOutput && (
                <div>
                  <p className="text-xs font-semibold text-zinc-400 mb-1">Tool Output</p>
                  <pre className="text-xs text-zinc-300 bg-zinc-950 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words font-mono">
                    {step.toolOutput}
                  </pre>
                </div>
              )}
              {step.errorMessage && (
                <div className="bg-red-500/10 border border-red-500/20 rounded p-2">
                  <p className="text-xs font-semibold text-red-400 mb-1">Error</p>
                  <p className="text-xs text-red-300">{step.errorMessage}</p>
                </div>
              )}
            </div>
          </details>
        </li>
      ))}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Job Trace Drawer
// ---------------------------------------------------------------------------

function JobTraceDrawer({
  jobId,
  onClose,
  onCancelClick,
}: {
  jobId: string | null;
  onClose: () => void;
  onCancelClick: (jobId: string, currentStatus: string) => void;
}) {
  const [trace, setTrace] = useState<JobTraceResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [slowLoad, setSlowLoad] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) return;
    startTransition(() => {
      setTrace(null);
      setError(null);
      setIsLoading(true);
      setSlowLoad(false);
    });

    const slowTimer = setTimeout(() => setSlowLoad(true), 5000);

    getJobTrace(jobId)
      .then((result) => setTrace(result))
      .catch((err: unknown) => {
        const is404 = err instanceof Error && /HTTP 404/.test(err.message);
        setError(
          is404
            ? 'Job trace not found — it may have been purged or was never recorded'
            : err instanceof Error
              ? err.message
              : 'Failed to load trace',
        );
      })
      .finally(() => {
        setIsLoading(false);
        clearTimeout(slowTimer);
      });

    return () => clearTimeout(slowTimer);
  }, [jobId]);

  const summary = trace?.summary;
  const title = summary ? (
    <div className="space-y-0.5">
      <p className="text-xs font-mono text-zinc-400 truncate" title={jobId ?? ''}>
        {jobId}
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        {summary && <StatusPill status={summary.status} />}
        {summary?.creditsActual !== null && summary?.creditsActual !== undefined && (
          <span className="text-xs text-zinc-400">
            {summary.creditsActual} credits
          </span>
        )}
        <span className="text-xs text-zinc-500">
          {formatDate(summary?.startedAt ?? null)}
        </span>
      </div>
    </div>
  ) : (
    jobId ?? 'Job Details'
  );

  return (
    <Drawer open={!!jobId} onClose={onClose} title={title}>
      {isLoading && (
        <div className="space-y-3">
          {slowLoad && (
            <p className="text-xs text-zinc-400 italic">
              Loading trace from cold storage…
            </p>
          )}
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-10 bg-zinc-800 rounded animate-pulse" />
          ))}
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded p-3 text-xs text-red-300">
          {error}
        </div>
      )}

      {!isLoading && !error && trace && (
        <div className="space-y-4">
          {/* Summary header */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-zinc-500">Profile</span>
              <p className="text-zinc-300 font-mono truncate">{trace.summary.profileId}</p>
            </div>
            <div>
              <span className="text-zinc-500">Output Type</span>
              <p className="text-zinc-300">{trace.summary.outputType}</p>
            </div>
            <div>
              <span className="text-zinc-500">Duration</span>
              <p className="text-zinc-300">{formatDuration(trace.summary.durationMs)}</p>
            </div>
            <div>
              <span className="text-zinc-500">Completed</span>
              <p className="text-zinc-300">{formatDate(trace.summary.completedAt)}</p>
            </div>
            <div>
              <span className="text-zinc-500">Credits Est / Actual</span>
              <p className="text-zinc-300">
                {trace.summary.creditsEstimated ?? '—'} / {trace.summary.creditsActual ?? '—'}
              </p>
            </div>
            <div>
              <span className="text-zinc-500">Quality</span>
              <p>
                <QualityPill
                  verdict={trace.summary.qualityVerdict}
                  score={trace.summary.qualityScore}
                />
              </p>
            </div>
          </div>

          {/* Judge feedback — WS-3 item 2.1 */}
          {trace.summary.judgeFeedback && (
            <div className="border border-zinc-800 rounded p-3">
              <p className="text-xs font-semibold text-zinc-400 mb-1">
                Judge Feedback
                {trace.summary.judgeTokens !== null && trace.summary.judgeTokens !== undefined && (
                  <span className="ml-2 font-normal text-zinc-600">
                    ({trace.summary.judgeTokens.toLocaleString()} judge tokens — platform overhead)
                  </span>
                )}
              </p>
              <pre className="text-xs text-zinc-300 bg-zinc-950 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words font-mono">
                {trace.summary.judgeFeedback}
              </pre>
            </div>
          )}

          {/* Deliverable */}
          {trace.deliverable && (
            <div className="border border-zinc-800 rounded p-3">
              <p className="text-xs font-semibold text-zinc-400 mb-2">Deliverable</p>
              <a
                href={trace.deliverable.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-violet-400 hover:text-violet-300 underline"
              >
                View deliverable
              </a>
              {trace.deliverable.previewText && (
                <p className="mt-2 text-xs text-zinc-400 line-clamp-3">
                  {trace.deliverable.previewText}
                </p>
              )}
            </div>
          )}

          {/* Failure section */}
          {trace.summary.status === 'Failed' && (
            <div className="bg-red-500/10 border border-red-500/20 rounded p-3 text-xs">
              <p className="font-semibold text-red-400 mb-1">Failure Details</p>
              <p className="text-red-300">
                Job failed — see step trace below for error details.
              </p>
            </div>
          )}

          {/* Running badge */}
          {trace.summary.status === 'Running' && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded p-2 text-xs text-blue-300">
              Live trace — new steps will appear when the drawer is reopened.
            </div>
          )}

          {/* Cancel Job action — only shown for Running or Queued */}
          {(trace.summary.status === 'Running' || trace.summary.status === 'Queued') && (
            <div className="pt-1">
              <button
                type="button"
                onClick={() => onCancelClick(trace.summary.jobId, trace.summary.status)}
                aria-label={`Cancel job ${trace.summary.jobId}`}
                className="min-h-[48px] w-full px-4 py-2 rounded-lg border border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300 hover:border-red-400 text-sm font-medium transition-colors"
              >
                Cancel Job
              </button>
            </div>
          )}

          {/* Step trace */}
          <div>
            <p className="text-xs font-semibold text-zinc-400 mb-2">
              Step Trace ({trace.steps.length} steps)
            </p>
            <StepTimeline steps={trace.steps} />
          </div>
        </div>
      )}
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: JobStatus[] = ['Queued', 'Running', 'Blocked', 'Completed', 'Failed'];
const VERDICT_OPTIONS: QualityVerdict[] = ['passed', 'failed_flagged', 'skipped'];

export function JobHistoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL state
  const openJobId = searchParams.get('jobId');

  // Filters
  const [timeWindow, setTimeWindow] = useState<TimeWindow>(loadStoredWindow);
  const [profileId, setProfileId] = useState(searchParams.get('profileId') ?? '');
  const [selectedStatuses, setSelectedStatuses] = useState<Set<JobStatus>>(new Set());
  const [selectedOutputTypes, setSelectedOutputTypes] = useState<Set<string>>(new Set());
  const [selectedVerdict, setSelectedVerdict] = useState<QualityVerdict | null>(null);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('startedAt');
  const [sortDesc, setSortDesc] = useState(true);

  // Data
  const [jobs, setJobs] = useState<AgentJobSummaryDto[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Output-type options — no server-provided list, so accumulate distinct
  // values seen across loaded pages (persists selections beyond one page).
  const [outputTypeOptions, setOutputTypeOptions] = useState<string[]>([]);

  // Cancel Job modal
  const [cancelTarget, setCancelTarget] = useState<{ jobId: string; currentStatus: string } | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const handleDismissToast = useCallback(() => setToast(null), []);

  // Export CSV (US-17-GR-29)
  const [exporting, setExporting] = useState(false);

  async function handleExport() {
    setExporting(true);
    try {
      const result = await exportJobsCsv({
        window: timeWindow,
        profileId: profileId || undefined,
        status: selectedStatuses.size > 0 ? [...selectedStatuses].join(',') : undefined,
        outputType: selectedOutputTypes.size > 0 ? [...selectedOutputTypes].join(',') : undefined,
        qualityVerdict: selectedVerdict ?? undefined,
        sortBy,
        sortDesc,
      });
      triggerCsvBlobDownload(buildCsvFilename('jobs', `window${timeWindow}`), result.blob);
      if (result.truncated) {
        let message: string;
        if (result.rowCount !== null && result.totalMatched !== null) {
          message = `Export capped at ${result.rowCount.toLocaleString()} of ${result.totalMatched.toLocaleString()} matching rows — narrow filters for a complete export.`;
        } else if (result.rowCount !== null) {
          message = `Export capped at ${result.rowCount.toLocaleString()} rows — narrow filters for a complete export.`;
        } else {
          message = 'Export capped at the server row limit — narrow filters for a complete export.';
        }
        setToast({
          variant: 'info',
          message,
          durationMs: 6000,
        });
      }
    } catch (err: unknown) {
      const is404 = err instanceof Error && /HTTP 404/.test(err.message);
      setToast({
        variant: 'error',
        message: is404
          ? 'Export endpoint is not available yet — try again shortly.'
          : err instanceof Error
            ? err.message
            : 'Failed to export jobs',
      });
    } finally {
      setExporting(false);
    }
  }

  const beginLoad = useLatestRequest();

  const fetchJobs = useCallback(async () => {
    const isCurrent = beginLoad();
    const params: GetJobsParams = {
      window: timeWindow,
      page,
      pageSize: 50,
      sortBy,
      sortDesc,
      profileId: profileId || undefined,
      status: selectedStatuses.size > 0 ? [...selectedStatuses].join(',') : undefined,
      outputType: selectedOutputTypes.size > 0 ? [...selectedOutputTypes].join(',') : undefined,
      qualityVerdict: selectedVerdict ?? undefined,
    };
    setIsLoading(true);
    setError(null);
    try {
      const result = await getJobs(params);
      if (!isCurrent()) return;
      setJobs(result.items);
      setTotal(result.total);
      setOutputTypeOptions((prev) => {
        const next = new Set(prev);
        result.items.forEach((job) => next.add(job.outputType));
        return [...next].sort();
      });
    } catch (err: unknown) {
      if (!isCurrent()) return;
      setError(err instanceof Error ? err.message : 'Failed to load jobs');
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  }, [beginLoad, timeWindow, page, sortBy, sortDesc, profileId, selectedStatuses, selectedOutputTypes, selectedVerdict]);

  useEffect(() => {
    startTransition(() => { void fetchJobs(); });
  }, [fetchJobs]);

  function openDrawer(jobId: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('jobId', jobId);
      return next;
    });
  }

  function closeDrawer() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('jobId');
      return next;
    });
  }

  async function handleCancelConfirm(reason: string) {
    if (!cancelTarget) return;
    try {
      const result = await cancelJob(cancelTarget.jobId, reason);
      if (result.alreadyCancelled) {
        setToast({ variant: 'info', message: 'Job already cancelled (idempotent)' });
      } else {
        const refundPart = result.refundAmount !== undefined && result.refundAmount !== null
          ? ` — ${result.refundAmount} credits refunded`
          : '';
        setToast({ variant: 'success', message: `Job ${cancelTarget.jobId} cancelled${refundPart}` });
      }
    } catch (err: unknown) {
      if (err instanceof JobAlreadyTerminalError) {
        setToast({ variant: 'info', message: 'Job is already in a terminal state' });
      } else {
        setToast({
          variant: 'error',
          message: err instanceof Error ? err.message : 'Failed to cancel job',
        });
      }
    } finally {
      setCancelTarget(null);
    }
  }

  function handleSort(key: string, desc: boolean) {
    setSortBy(key);
    setSortDesc(desc);
    setPage(1);
  }

  function toggleStatus(s: JobStatus) {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
    setPage(1);
  }

  function toggleVerdict(v: QualityVerdict) {
    setSelectedVerdict((prev) => (prev === v ? null : v));
    setPage(1);
  }

  function toggleOutputType(t: string) {
    setSelectedOutputTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
    setPage(1);
  }

  function handleWindowChange(w: TimeWindow) {
    setTimeWindow(w);
    saveStoredWindow(w);
    setPage(1);
  }

  // Summary strip — derived from the currently loaded (filtered) page of jobs.
  const pageSuccessCount = jobs.filter((j) => j.status === 'Completed').length;
  const pageFailureCount = jobs.filter((j) => j.status === 'Failed').length;
  const pageSuccessRate = jobs.length > 0 ? (pageSuccessCount / jobs.length) * 100 : null;
  const pageTotalUsd = jobs.length > 0 ? jobs.reduce((sum, j) => sum + (j.estimatedUsd ?? 0), 0) : null;

  const columns: Column<AgentJobSummaryDto>[] = [
    {
      key: 'startedAt',
      label: 'Started',
      sortable: true,
      render: (row) => <span className="text-xs font-mono">{formatDate(row.startedAt)}</span>,
    },
    {
      key: 'jobId',
      label: 'Job ID',
      render: (row) => (
        <span className="text-xs font-mono text-zinc-400" title={row.jobId}>
          {truncate(row.jobId)}
        </span>
      ),
    },
    {
      key: 'profileId',
      label: 'Profile',
      sortable: true,
      render: (row) => (
        <Link
          to={`/reports/users/${row.profileId}`}
          onClick={(e) => e.stopPropagation()}
          title={row.profileId}
          className="text-xs font-mono text-violet-400 hover:text-violet-300 underline-offset-2 hover:underline"
        >
          {truncate(row.profileId)}
        </Link>
      ),
    },
    {
      key: 'outputType',
      label: 'Output Type',
      sortable: true,
      render: (row) => <span className="text-xs">{row.outputType}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => <StatusPill status={row.status} />,
    },
    {
      key: 'quality',
      label: 'Quality',
      render: (row) => (
        <QualityPill verdict={row.qualityVerdict} score={row.qualityScore} />
      ),
    },
    {
      key: 'credits',
      label: 'Credits Est / Actual',
      sortable: true,
      render: (row) => (
        <span className="text-xs text-zinc-400">
          {row.creditsEstimated ?? '—'} / {row.creditsActual ?? '—'}
        </span>
      ),
    },
    {
      key: 'estimatedUsd',
      label: <AbbrLabel term="Est. USD" />,
      render: (row) => (
        <span className="text-xs text-zinc-400 font-mono whitespace-nowrap">
          {formatUsd(row.estimatedUsd)}
        </span>
      ),
    },
    {
      key: 'model',
      label: 'Model',
      render: (row) =>
        row.model ? (
          <span className="text-xs text-zinc-400 font-mono whitespace-nowrap" title={row.model}>
            {truncate(row.model, 18)}
          </span>
        ) : (
          <span className="text-xs text-zinc-600">—</span>
        ),
    },
    {
      key: 'error',
      label: 'Error',
      render: (row) =>
        row.errorSummary ? (
          <span
            className="text-xs text-red-300/90 whitespace-nowrap"
            title={row.errorSummary}
          >
            {truncate(row.errorSummary, 40)}
          </span>
        ) : (
          <span className="text-xs text-zinc-600">—</span>
        ),
    },
    {
      key: 'durationMs',
      label: 'Duration',
      sortable: true,
      render: (row) => (
        <span className="text-xs text-zinc-400">{formatDuration(row.durationMs)}</span>
      ),
    },
  ];

  return (
    <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 px-6 py-3 border-b border-zinc-800 bg-zinc-950">
        <TimeWindowSelector value={timeWindow} onChange={handleWindowChange} />

        <input
          type="text"
          value={profileId}
          onChange={(e) => { setProfileId(e.target.value); setPage(1); }}
          placeholder="Filter by profileId…"
          className="px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-200 placeholder:text-zinc-500 focus:outline-none focus:border-violet-500 min-h-[48px] w-52"
          aria-label="Filter by profileId"
        />

        <div className="flex items-center gap-1" role="group" aria-label="Filter by status">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => toggleStatus(s)}
              aria-pressed={selectedStatuses.has(s)}
              className={[
                'px-2 py-1 rounded-full text-xs font-medium border transition-colors min-h-[48px]',
                selectedStatuses.has(s)
                  ? 'bg-violet-600 text-zinc-950 border-violet-500'
                  : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:bg-zinc-700',
              ].join(' ')}
            >
              {s}
            </button>
          ))}
        </div>

        {outputTypeOptions.length > 0 && (
          <div className="flex items-center gap-1" role="group" aria-label="Filter by output type">
            {outputTypeOptions.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => toggleOutputType(t)}
                aria-pressed={selectedOutputTypes.has(t)}
                className={[
                  'px-2 py-1 rounded-full text-xs font-medium border transition-colors min-h-[48px]',
                  selectedOutputTypes.has(t)
                    ? 'bg-violet-600 text-zinc-950 border-violet-500'
                    : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:bg-zinc-700',
                ].join(' ')}
              >
                {t}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-1" role="group" aria-label="Filter by quality verdict">
          {VERDICT_OPTIONS.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => toggleVerdict(v)}
              aria-pressed={selectedVerdict === v}
              className={[
                'px-2 py-1 rounded-full text-xs font-medium border transition-colors min-h-[48px]',
                selectedVerdict === v
                  ? 'bg-violet-600 text-zinc-950 border-violet-500'
                  : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:bg-zinc-700',
              ].join(' ')}
            >
              {VERDICT_LABELS[v]}
            </button>
          ))}
        </div>

        {(profileId || selectedStatuses.size > 0 || selectedOutputTypes.size > 0 || selectedVerdict) && (
          <button
            type="button"
            onClick={() => {
              setProfileId('');
              setSelectedStatuses(new Set());
              setSelectedOutputTypes(new Set());
              setSelectedVerdict(null);
              setPage(1);
            }}
            className="text-xs text-zinc-400 hover:text-zinc-200 min-h-[48px]"
          >
            Clear filters
          </button>
        )}

        <button
          type="button"
          onClick={() => void handleExport()}
          disabled={exporting || isLoading || total === 0}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-zinc-800 text-zinc-200 hover:bg-zinc-700 transition-colors disabled:opacity-50 min-h-[48px]"
        >
          {exporting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Export CSV
        </button>

        <span className="text-xs text-zinc-500">
          {total.toLocaleString()} jobs
        </span>
      </div>

      {/* Error banner */}
      {error && (
        <div className="px-6 py-2 bg-red-500/10 border-b border-red-500/20 text-red-300 text-xs">
          {error}
        </div>
      )}

      {/* Summary strip — total from the API, everything else is page-scoped */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 px-6 py-2 border-b border-zinc-800 bg-zinc-950 text-xs text-zinc-400">
        <span>
          <span className="text-zinc-200 font-medium">{total.toLocaleString()}</span> total jobs
        </span>
        <span>
          <span className="text-zinc-200 font-medium">
            {pageSuccessRate !== null ? `${pageSuccessRate.toFixed(1)}%` : '—'}
          </span>{' '}
          success rate (this page)
        </span>
        <span>
          <span className="text-zinc-200 font-medium">{pageFailureCount}</span> failed (this page)
        </span>
        <span>
          <span className="text-zinc-200 font-medium">{formatUsd(pageTotalUsd)}</span> <AbbrLabel term="Est. USD" /> (this page)
        </span>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <Table
          columns={columns}
          rows={jobs}
          keyFn={(row) => row.jobId}
          onRowClick={(row) => openDrawer(row.jobId)}
          isLoading={isLoading}
          emptyMessage="No orchestrator runs in the selected window. Try expanding to 90 days or clearing filters."
          page={page}
          pageSize={50}
          total={total}
          onPageChange={setPage}
          sortBy={sortBy}
          sortDesc={sortDesc}
          onSort={handleSort}
        />
      </div>

      {/* Drawer */}
      <JobTraceDrawer
        jobId={openJobId}
        onClose={closeDrawer}
        onCancelClick={(jobId, currentStatus) => setCancelTarget({ jobId, currentStatus })}
      />

      {/* Cancel Job confirmation modal */}
      {cancelTarget && (
        <CancelJobModal
          isOpen={true}
          jobId={cancelTarget.jobId}
          currentStatus={cancelTarget.currentStatus}
          onConfirm={handleCancelConfirm}
          onCancel={() => setCancelTarget(null)}
        />
      )}

      {/* Toast notifications */}
      <Toast toast={toast} onDismiss={handleDismissToast} />
    </main>
  );
}
